/**
 * Editable Cell component for inline table editing
 */
import React, { useState, useEffect, useRef } from 'react';

const EditableCell = ({
  value,
  type = 'text',
  options = [],
  onChange,
  disabled = false,
  placeholder = '',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (type === 'text' || type === 'number') {
        inputRef.current.select();
      }
    }
  }, [isEditing, type]);

  const handleSave = () => {
    if (editValue !== value) {
      onChange(editValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Value already updated via onChange, just close editing
      setIsEditing(false);
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (disabled) {
    return <span className="text-gray-500">{value || '-'}</span>;
  }

  if (!isEditing) {
    return (
      <div
        className="editable-cell px-2 py-1 cursor-pointer bg-white border border-gray-300 rounded hover:border-primary-400 text-xs"
        style={{ minHeight: '24px' }}
        onClick={() => setIsEditing(true)}
      >
        {value || <span className="text-gray-400">{placeholder || 'Click to edit'}</span>}
      </div>
    );
  }

  if (type === 'select') {
    return (
      <select
        ref={inputRef}
        className="select"
        value={editValue || ''}
        onChange={(e) => {
          setEditValue(e.target.value);
          // Immediately trigger onChange for dropdowns so drag-to-fill works
          onChange(e.target.value);
        }}
        onBlur={() => setIsEditing(false)}
        onKeyDown={handleKeyDown}
      >
        <option value="">Select...</option>
        {options.map((option, index) => (
          <option key={index} value={option.value || option}>
            {option.label || option}
          </option>
        ))}
      </select>
    );
  }

  if (type === 'checkbox') {
    return (
      <input
        ref={inputRef}
        type="checkbox"
        className="w-4 h-4"
        checked={editValue || false}
        onChange={(e) => {
          setEditValue(e.target.checked);
          onChange(e.target.checked);
          setIsEditing(false);
        }}
        onBlur={() => setIsEditing(false)}
      />
    );
  }

  return (
    <input
      ref={inputRef}
      type={type}
      className="input"
      value={editValue || ''}
      onChange={(e) => {
        const newValue = e.target.value;
        setEditValue(newValue);
        // Immediately trigger onChange so drag-to-fill gets the current value
        onChange(newValue);
      }}
      onBlur={() => setIsEditing(false)}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      step={type === 'number' ? 'any' : undefined}
    />
  );
};

export default EditableCell;
